import { api, ApiError, authHeaders, BASE } from './client';

/**
 * Avatar API client (2026-06-18). Built-in face selection is now synced
 * server-side (so the moderation portal can see every player's avatar), and
 * custom images are uploaded for review. The image bytes are served from the
 * gated {@code /v1/avatars/{id}} endpoint — a PENDING image needs the owning
 * {@code playerId} on the URL; an APPROVED one is public.
 */

export type AvatarReviewStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

/** What the server returns for the player's own avatar state. */
export interface AvatarState {
  builtinId: string | null;
  activeCustomUploadId: string | null;
  pendingUploadId: string | null;
  lastUploadStatus: AvatarReviewStatus | null;
  rejectionReason: string | null;
}

/** A custom upload row (returned by the upload endpoint). */
export interface AvatarUpload {
  id: string;
  playerId: string;
  contentType: string;
  byteSize: number;
  status: AvatarReviewStatus;
  rejectionReason: string | null;
  reviewedBy: string | null;
  reviewedAt: number | null;
  created: number | null;
}

/** Persist the chosen built-in face id. The server also clears any active
 *  custom avatar — picking a built-in face means "use this, not my upload." */
export async function putAvatarSelection(playerId: string, builtinId: string): Promise<void> {
  await api<void>(`/players/${encodeURIComponent(playerId)}/avatar`, {
    method: 'PUT',
    body: JSON.stringify({ builtinId }),
  });
}

/** Read the player's avatar state — used to rehydrate the custom image (and any
 *  pending/rejected status) on a cold launch. */
export function getAvatarState(playerId: string): Promise<AvatarState> {
  return api<AvatarState>(`/players/${encodeURIComponent(playerId)}/avatar`);
}

/**
 * Upload a custom image for review. Multipart, so this bypasses the JSON
 * {@link api} wrapper (which forces an application/json content type) and lets
 * the browser set the multipart boundary itself.
 */
export async function uploadCustomAvatar(
  playerId: string,
  blob: Blob,
  filename = 'avatar.jpg',
): Promise<AvatarUpload> {
  const form = new FormData();
  form.append('file', blob, filename);
  const res = await fetch(
    `${BASE}/players/${encodeURIComponent(playerId)}/avatar/upload`,
    { method: 'POST', body: form, headers: await authHeaders() },
  );
  if (!res.ok) {
    throw new ApiError(res.status, await res.text().catch(() => ''));
  }
  return (await res.json()) as AvatarUpload;
}

/** Build the serve URL for an upload id. Pass {@code playerId} so the owner can
 *  view their own image while it's still PENDING (harmless once APPROVED). */
export function avatarImageUrl(uploadId: string, playerId?: string | null): string {
  const base = `${BASE}/avatars/${encodeURIComponent(uploadId)}`;
  return playerId ? `${base}?playerId=${encodeURIComponent(playerId)}` : base;
}
